import React, {
    useEffect,
    useContext,
    useCallback,
    useMemo,
    useState,
    useRef,
    useLayoutEffect,
} from "react";
import axios from "axios";
import WebSocketService from "../../context/WebSocketService";

import MessageContainer from "./MessageContainer";
import { GlobalContext } from "../../context/GlobalContext";
const PUNCTUATION_REGEX = /^[\p{P}\p{S}]+$/u;

function ConversationArea({
    preferences,
    setPreferences,
    conversationHistory,
    setConversationHistory,
    conversationDetails,
}) {
    const { auth, viewerMode } = useContext(GlobalContext);
    const { userId, userType } = auth;
    const [activeMessageId, setActiveMessageId] = useState(null);
    const [wordBank, setWordBank] = useState([]);
    const [expandedMessageId, setExpandedMessageId] = useState(null);
    const [selectedMorphemes, setSelectedMorphemes] = useState([]);
    const [anchorMessageId, setAnchorMessageId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const scrollContainerRef = useRef(null);
    const isAtBottomRef = useRef(true);

    useEffect(() => {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
            document.documentElement.classList.add("ios");
        }
    }, []);

    // Fetch initial morphemes.
    const fetchMorphemes = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/morphemes`, {
                withCredentials: true,
            });
            const sortedData = response.data.morphemes.sort(
                (a, b) => b.trueCount - a.trueCount
            );
            const masteryThreshold = response.data.masteryThreshold;
            setPreferences((prev) => ({
                ...prev,
                masteryThreshold,
            }));
            setWordBank(sortedData);
            setAnchorMessageId(response.data.anchorMessageId);
            return sortedData;
        } catch (error) {
            console.error("Error fetching morphemes:", error);
            throw error;
        }
    }, [setPreferences]);

    useEffect(() => {
        const { conversationHistory } = conversationDetails;
        const lastMessageId = conversationHistory[conversationHistory.length - 1]?.id || null;
        setAnchorMessageId(lastMessageId);
    }, [conversationDetails]);

    useEffect(() => {
        fetchMorphemes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const threshold = 24;
        const handleScroll = () => {
            const distanceFromBottom =
                container.scrollHeight - container.scrollTop - container.clientHeight;
            isAtBottomRef.current = distanceFromBottom <= threshold;
        };
        handleScroll();
        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const addSnapshotToHistory = useCallback(
        (snapshot) => {
            const {
                senderId,
                target: messageId,
                fieldName,
                status,
                statusMessage,
                value,
                senderType,
                conversationId,
            } = snapshot;
            if (messageId === "scenarioSnapshot") return;
            if (conversationId && conversationId !== conversationDetails.conversationId)
                return;
            setConversationHistory((prevHistory) => {
                // Work on a shallow copy so we can reassign positions as needed.
                let updatedHistory = [...prevHistory];
                const messageExists = updatedHistory.some((msg) => msg.id === messageId);
                if (messageExists) {
                    // Update an existing message.
                    updatedHistory = updatedHistory.map((msg) =>
                        msg.id === messageId
                            ? { ...msg, [fieldName]: { status, statusMessage, value } }
                            : msg
                    );
                } else {
                    // First, check for a temporary message for the same sender.
                    const tempIndex = updatedHistory.findIndex(
                        (msg) => msg.id === `temp-${senderType}`
                    );
                    if (tempIndex !== -1) {
                        // Replace the temporary message with the new completed one.
                        updatedHistory[tempIndex] = {
                            ...updatedHistory[tempIndex],
                            senderId,
                            id: messageId,
                            senderType,
                            [fieldName]: { status, statusMessage, value },
                        };
                    } else {
                        // Determine the insertion point.
                        // For a non-temp message, if the last message is a temp message
                        // from the opposite sender, insert the new message just before it.
                        const otherSenderType = senderType === "user" ? "assistant" : "user";
                        if (
                            updatedHistory.length > 0 &&
                            updatedHistory[updatedHistory.length - 1].id ===
                                `temp-${otherSenderType}`
                        ) {
                            updatedHistory.splice(updatedHistory.length - 1, 0, {
                                id: messageId,
                                senderId,
                                senderType,
                                [fieldName]: { status, statusMessage, value },
                            });
                        } else {
                            // Otherwise, append to the end.
                            updatedHistory.push({
                                id: messageId,
                                senderId,
                                senderType,
                                [fieldName]: { status, statusMessage, value },
                            });
                        }
                    }
                }
                return updatedHistory;
            });
        },
        [conversationDetails.conversationId, setConversationHistory]
    );

    useEffect(() => {
        const handleTargetMessage = (data) => {
            //console.log("data", data);
            addSnapshotToHistory(data);
        };
        WebSocketService.on("message:target", handleTargetMessage);
        return () => {
            WebSocketService.off("message:target", handleTargetMessage);
        };
    }, [addSnapshotToHistory]);

    const processMorphemeDetails = (morphemeDetails = {}) =>
        Object.values(morphemeDetails)
            .map(([originalMorpheme, transliteratedMorpheme, translatedMorpheme]) => ({
                originalMorpheme,
                transliteratedMorpheme,
                translatedMorpheme,
            }))
            .filter(
                ({ originalMorpheme, transliteratedMorpheme }) =>
                    originalMorpheme && transliteratedMorpheme
            );

    const normalizeMorpheme = (value) =>
        value ? value.toLowerCase().replace(/[\s\p{P}]/gu, "") : null;

    const getAggregatedMorphemesAfter = useCallback(
        (expandedId) => {
            const index = conversationHistory.findIndex(
                (msg) => msg.id === expandedId || msg._id === expandedId
            );
            if (index === -1) return [];
            const messagesAfter = conversationHistory.slice(index + 1);
            return messagesAfter.flatMap((msg) => {
                const morphemes = msg.morphemeTranslationDetails?.value
                    ? processMorphemeDetails(msg.morphemeTranslationDetails.value)
                    : [];
                return morphemes.map((morpheme) =>
                    normalizeMorpheme(morpheme.originalMorpheme)
                );
            });
        },
        [conversationHistory]
    );

    const applyOptimisticMorphemeUpdate = useCallback(
        (morphemes, expandedId, isMastered) => {
            if (!morphemes?.length) return;
            const normalizedSelection = morphemes
                .map((morpheme) => normalizeMorpheme(morpheme))
                .filter(Boolean);
            if (!normalizedSelection.length) return;
            const aggregatedMorphemes = getAggregatedMorphemesAfter(expandedId);
            const aggregatedCounts = aggregatedMorphemes.reduce((acc, morpheme) => {
                if (!morpheme) return acc;
                acc[morpheme] = (acc[morpheme] || 0) + 1;
                return acc;
            }, {});
            const selectedSet = new Set(normalizedSelection);
            setWordBank((prevWordBank) =>
                prevWordBank.map((word) => {
                    const normalizedWord = normalizeMorpheme(word.originalMorpheme);
                    if (!normalizedWord || !selectedSet.has(normalizedWord)) {
                        return word;
                    }
                    const extra = aggregatedCounts[normalizedWord] || 0;
                    const baseCount = isMastered ? preferences.masteryThreshold : 1;
                    const targetCount = baseCount + extra;
                    if (isMastered) {
                        if (word.effectiveCount >= targetCount) return word;
                        return {
                            ...word,
                            effectiveCount: targetCount,
                        };
                    }
                    const newCount = Math.min(word.effectiveCount, targetCount);
                    if (newCount === word.effectiveCount) return word;
                    return {
                        ...word,
                        effectiveCount: newCount,
                    };
                })
            );
        },
        [getAggregatedMorphemesAfter, preferences.masteryThreshold]
    );

    // Calculate opacity based on cumulative count and masteryThreshold.
    function calculateOpacity(cumulativeCount, masteryThreshold) {
        if (cumulativeCount <= 0) {
            /*console.debug(
                `calculateOpacity: cumulativeCount=${cumulativeCount} (<=0), opacity=0`
            ); */
            return 1;
        }
        const opacity = 1 - Math.min(cumulativeCount / masteryThreshold, 1);
        /*console.debug(
            `calculateOpacity: cumulativeCount=${cumulativeCount}, masteryThreshold=${masteryThreshold}, opacity=${opacity}`
        ); */
        return opacity;
    }

    // Process "old" messages (from anchor backwards) with selected morpheme highlighting.
    function processOldMessages(
        oldMessages,
        baseWordCounts,
        masteryThreshold,
        selectedMorphemes
    ) {
        const cumulativeCounts = { ...baseWordCounts };
        const selectedSet = new Set(selectedMorphemes.map((m) => m.toLowerCase()));
        const processedReversed = oldMessages
            .slice()
            .reverse()
            .map((msg) => {
                const newMsg = { ...msg };
                const morphemes = msg.morphemeTranslationDetails?.value
                    ? processMorphemeDetails(msg.morphemeTranslationDetails.value)
                    : [];
                const updatedMorphemes = morphemes.map((morpheme, index) => {
                    const key = `${msg.id}-old-${index}`;
                    const mKey = morpheme.originalMorpheme.toLowerCase();
                    const opacity = calculateOpacity(
                        cumulativeCounts[mKey] || 1,
                        masteryThreshold
                    );
                    cumulativeCounts[mKey] = (cumulativeCounts[mKey] || 0) - 1;
                    const isPunctuation = PUNCTUATION_REGEX.test(
                        morpheme.transliteratedMorpheme
                    );
                    const newTranslated =
                        opacity === 0 || isPunctuation ? "" : morpheme.translatedMorpheme;
                    return {
                        ...morpheme,
                        opacity,
                        key,
                        translatedMorpheme: newTranslated,
                        isSelected: selectedSet.has(mKey),
                    };
                });
                newMsg.morphemeArray = updatedMorphemes;
                return newMsg;
            });
        const processedOldMessages = processedReversed.reverse();
        return { processedOldMessages, cumulativeCounts };
    }

    // Process "new" messages (after anchor) with selected morpheme highlighting.
    function processNewMessages(
        newMessages,
        baseWordCounts,
        masteryThreshold,
        selectedMorphemes
    ) {
        const cumulativeCounts = { ...baseWordCounts };
        const selectedSet = new Set(selectedMorphemes.map((m) => m.toLowerCase()));
        const processedNewMessages = newMessages.map((msg) => {
            const newMsg = { ...msg };
            const morphemes = msg.morphemeTranslationDetails?.value
                ? processMorphemeDetails(msg.morphemeTranslationDetails.value)
                : [];
            const updatedMorphemes = morphemes.map((morpheme, index) => {
                const key = `${msg.id}-new-${index}`;
                const mKey = morpheme.originalMorpheme.toLowerCase();
                cumulativeCounts[mKey] = (cumulativeCounts[mKey] || 0) + 1;
                const opacity = calculateOpacity(cumulativeCounts[mKey], masteryThreshold);
                /*                 console.debug(
                    `NewMessage: msgId=${msg.id}, morpheme=${mKey}, count=${cumulativeCounts[mKey]}, opacity=${opacity}`
                ); */
                const isPunctuation = PUNCTUATION_REGEX.test(morpheme.originalMorpheme);
                const newTranslated =
                    opacity === 0 || isPunctuation ? "" : morpheme.translatedMorpheme;
                return {
                    ...morpheme,
                    opacity,
                    key,
                    translatedMorpheme: newTranslated,
                    isSelected: selectedSet.has(mKey),
                };
            });
            newMsg.morphemeArray = updatedMorphemes;
            return newMsg;
        });
        return { processedNewMessages, cumulativeCounts };
    }

    // Process the entire conversation by splitting into old (up to anchor) and new messages.
    function processConversationOpacity(
        conversationHistory,
        baseWordCounts,
        masteryThreshold,
        anchorMessageId,
        selectedMorphemes
    ) {
        const anchorIndex = conversationHistory.findIndex((msg) => msg.id === anchorMessageId);
        if (anchorIndex === -1) {
            const { processedNewMessages } = processNewMessages(
                conversationHistory,
                baseWordCounts,
                masteryThreshold,
                selectedMorphemes
            );
            return processedNewMessages;
        }
        const oldMessages = conversationHistory.slice(0, anchorIndex + 1);
        const newMessages = conversationHistory.slice(anchorIndex + 1);
        const { processedOldMessages } = processOldMessages(
            oldMessages,
            baseWordCounts,
            masteryThreshold,
            selectedMorphemes
        );
        const { processedNewMessages } = processNewMessages(
            newMessages,
            baseWordCounts,
            masteryThreshold,
            selectedMorphemes
        );
        return processedOldMessages.concat(processedNewMessages);
    }

    const formattedConversationHistory = useMemo(() => {
        if (!Array.isArray(conversationHistory)) {
            console.error("Invalid conversation history.");
            return [];
        }
        // Build base counts from the word bank.
        const baseWordCounts = wordBank.reduce((acc, word) => {
            const key = word.originalMorpheme.toLowerCase();
            acc[key] = word.effectiveCount;
            return acc;
        }, {});
        return processConversationOpacity(
            conversationHistory,
            baseWordCounts,
            preferences.masteryThreshold,
            anchorMessageId,
            selectedMorphemes
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        conversationHistory,
        wordBank,
        preferences.masteryThreshold,
        anchorMessageId,
        selectedMorphemes,
    ]);

    useLayoutEffect(() => {
        if (!isAtBottomRef.current) return;
        const container = scrollContainerRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
    }, [formattedConversationHistory]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const optionsContainer = document.querySelector(".message-options-container");
            if (!optionsContainer) return;
            if (!optionsContainer.contains(event.target)) {
                setActiveMessageId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCopyTargetMessage = useCallback(() => {
        if (!activeMessageId) return;
        const message = conversationHistory.find((msg) => msg.id === activeMessageId);
        if (!message) return;
        const textToCopy = message.targetLanguageMessage?.value || "";
        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {})
            .catch((error) => {
                console.error("Failed to copy message:", error);
            });
    }, [activeMessageId, conversationHistory]);

    const handleDeleteMessage = useCallback(async () => {
        if (!activeMessageId) return;

        // If confirmation not yet activated, set it and return
        if (!deleteConfirm) {
            setDeleteConfirm(true);
            return;
        }

        // If already confirmed, perform deletion
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/message/delete-message/${activeMessageId}`,
                { withCredentials: true }
            );
            // Optionally, clear the active message to close the menu
            setActiveMessageId(null);
        } catch (error) {
            console.error("Error deleting message:", error);
        }
    }, [activeMessageId, deleteConfirm]);

    // Reset confirmation when the menu closes
    useEffect(() => {
        if (activeMessageId === null) {
            setDeleteConfirm(false);
        }
    }, [activeMessageId]);

    const handleFullSentenceTranslation = useCallback(async () => {
        if (!activeMessageId) return;
        setActiveMessageId(null);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/message/translate-sentence`, {
                messageId: activeMessageId,
            });
        } catch (error) {
            console.error("Error translating sentence:", error);
        }
    }, [activeMessageId]);

    const handleWordOptions = useCallback((messageId) => {
        setActiveMessageId(null);
        setExpandedMessageId((prev) => (prev === messageId ? null : messageId));
    }, []);

    const hasConversationContent = useMemo(() => {
        return formattedConversationHistory.some((message) => {
            const targetText = message.targetLanguageMessage?.value?.trim();
            const originalText = message.originalMessage?.value?.trim();
            const fullSentenceText = message.fullSentenceTranslation?.value?.trim();
            return targetText || originalText || fullSentenceText;
        });
    }, [formattedConversationHistory]);

    const languageWarningMessage = useMemo(() => {
        if (!hasConversationContent) {
            return null;
        }

        const profileTargetLanguage = preferences?.language?.trim();
        const conversationTargetLanguage = conversationDetails?.targetLanguage?.trim();
        const persona = conversationDetails?.persona?.toLowerCase() || "";
        const title = conversationDetails?.title?.toLowerCase() || "";
        const isTutorialConversation =
            persona.includes("tutorial") || title.includes("tutorial");

        if (isTutorialConversation) {
            return null;
        }

        if (
            !profileTargetLanguage ||
            profileTargetLanguage.toLowerCase() === "not set"
        ) {
            return "The target language isn't set. Complete the tutorial or manually set your target language in the settings.";
        }

        if (
            conversationTargetLanguage &&
            profileTargetLanguage &&
            conversationTargetLanguage.toLowerCase() !==
                profileTargetLanguage.toLowerCase()
        ) {
            return `This conversation's target language is ${conversationTargetLanguage}. Start a new conversation to learn ${profileTargetLanguage}.`;
        }

        return null;
    }, [
        hasConversationContent,
        preferences?.language,
        conversationDetails?.targetLanguage,
        conversationDetails?.persona,
        conversationDetails?.title,
    ]);

    const warningMessage = useMemo(() => {
        if (!languageWarningMessage) {
            return null;
        }

        return {
            id: "language-warning",
            senderId: null,
            senderType: null,
            targetLanguageMessage: {
                status: "loaded",
                statusMessage: null,
                value: languageWarningMessage,
            },
            isWarning: true,
        };
    }, [languageWarningMessage]);

    const renderMessageOptions = useCallback(() => {
        if (viewerMode) return null;
        // Find the active message from the conversation history.
        const activeMessage = conversationHistory.find((msg) => msg.id === activeMessageId);
        // Determine if the message can be deleted.
        // Admins ignore restrictions.
        const canDelete =
            userType === "admin" ||
            (activeMessage &&
                (activeMessage.senderType === "assistant" ||
                    (activeMessage.senderType === "user" &&
                        activeMessage.senderId === userId)));

        return (
            <div className={`message-options-container ${activeMessageId ? "visible" : ""}`}>
                {canDelete && (
                    <md-outlined-button
                        className="button"
                        onClick={handleDeleteMessage}
                        title={deleteConfirm ? "Are you sure?" : "Delete Message"}
                        aria-label={deleteConfirm ? "Are you sure?" : "Delete Message"}
                    >
                        <md-icon
                            style={{
                                transform: "translateY(11.5%)",
                                fontSize: "1rem",
                                color: "var(--md-sys-color-error)",
                            }}
                        >
                            delete
                        </md-icon>
                        <span style={{ color: "var(--md-sys-color-error)" }}>
                            {deleteConfirm ? "Are you sure?" : "Delete Message"}
                        </span>
                    </md-outlined-button>
                )}

                <md-outlined-button
                    className="button"
                    onClick={handleCopyTargetMessage}
                    title="Copy Message"
                    aria-label="Copy Message"
                >
                    <md-icon style={{ transform: "translateY(11.5%)", fontSize: "1rem" }}>
                        content_copy
                    </md-icon>
                    Copy Message
                </md-outlined-button>

                <md-outlined-button
                    className="button"
                    onClick={handleFullSentenceTranslation}
                    title="Translate Message"
                    aria-label="Translate Message"
                >
                    <md-icon style={{ transform: "translateY(11.5%)", fontSize: "1rem" }}>
                        translate
                    </md-icon>
                    Full Sentence Translation
                </md-outlined-button>

                <md-outlined-button
                    className="button"
                    onClick={() => handleWordOptions(activeMessageId)}
                    title="Word Options"
                    aria-label="Word Options"
                >
                    <md-icon style={{ transform: "translateY(11.5%)", fontSize: "1rem" }}>
                        build_circle
                    </md-icon>
                    Word Options
                </md-outlined-button>
            </div>
        );
    }, [
        viewerMode,
        conversationHistory,
        userType,
        userId,
        activeMessageId,
        handleDeleteMessage,
        deleteConfirm,
        handleCopyTargetMessage,
        handleFullSentenceTranslation,
        handleWordOptions,
    ]);

    return (
        <div className="conversation-area" ref={scrollContainerRef}>
            <div className="message-list">
                {warningMessage && (
                    <MessageContainer
                        key={warningMessage.id}
                        isExpanded={false}
                        message={warningMessage}
                        preferences={preferences}
                        setActiveMessageId={setActiveMessageId}
                        wordBank={wordBank}
                        expandedMessageId={expandedMessageId}
                        setExpandedMessageId={setExpandedMessageId}
                        selectedMorphemes={selectedMorphemes}
                        setSelectedMorphemes={setSelectedMorphemes}
                        fetchMorphemes={fetchMorphemes}
                        onOptimisticMorphemeUpdate={applyOptimisticMorphemeUpdate}
                    />
                )}
                {formattedConversationHistory.map((message) => (
                    <MessageContainer
                        key={message.id}
                        isExpanded={expandedMessageId === message.id}
                        message={message}
                        preferences={preferences}
                        setActiveMessageId={setActiveMessageId}
                        wordBank={wordBank}
                        expandedMessageId={expandedMessageId}
                        setExpandedMessageId={setExpandedMessageId}
                        selectedMorphemes={selectedMorphemes}
                        setSelectedMorphemes={setSelectedMorphemes}
                        fetchMorphemes={fetchMorphemes}
                        onOptimisticMorphemeUpdate={applyOptimisticMorphemeUpdate}
                    />
                ))}
            </div>
            {activeMessageId !== null && renderMessageOptions()}
        </div>
    );
}

export default ConversationArea;
